import React from "react";
import {Button, Checkbox, InputAdornment, MenuItem, TextField, Tooltip} from "@mui/material";
import {
    AddLinkOutlined,
    CheckOutlined,
    EditOutlined,
    LinkOffOutlined,
    PlaylistAddOutlined,
    SearchOutlined,
} from "@mui/icons-material";
import type {ClContract} from "../contractLinkageData";
import {CL_CURRENCY, daysUntilExpiry} from "../contractLinkageData";
import type {ClContractEdits, ClContractPatch, ClPortfolioFilter, ClReviewTx} from "../contractLinkageUtils";
import {
    formatCompactCurrency,
    formatDate,
    formatFullCurrency,
    formatNumber,
    formatPeriod,
    formatPct,
    withinContractPeriod,
} from "../contractLinkageUtils";
import {ChannelChip, ConfidencePill, PeriodFit, StatusChip} from "./LinkageCells";
import {ContractOverview} from "./ContractOverview";
import {ContractEditDialog} from "./ContractEditDialog";
import {AddTransactionsDialog} from "./AddTransactionsDialog";

type Props = {
    /** Contracts with derived status and the user's edits applied. */
    contracts: ClContract[];
    /** Contracts as extracted (before edits), for diffing and reset. */
    baseContractsById: ReadonlyMap<string, ClContract>;
    edits: ClContractEdits;
    /** All transactions, decorated with their effective link & status. */
    rows: ClReviewTx[];
    /** Filter from the status cards above; narrows the contract list. */
    statusFilter: ClPortfolioFilter | null;
    onClearStatusFilter: () => void;
    onOpenTx: (tx: ClReviewTx) => void;
    onAccept: (txIds: string[]) => void;
    onUnlink: (txIds: string[]) => void;
    onAssignTo: (contractId: string, txIds: string[]) => void;
    onSaveContract: (contractId: string, patch: ClContractPatch | null) => void;
};

type ExplorerTab = 'overview' | 'linked' | 'candidates';

type DateField = 'date' | 'postingDate' | 'clearingDate';

const DATE_FIELDS: {value: DateField; label: string}[] = [
    {value: 'date', label: 'Invoice date'},
    {value: 'postingDate', label: 'Posting date'},
    {value: 'clearingDate', label: 'Clearing date'},
];

const STATUS_FILTER_LABEL: Record<ClPortfolioFilter, string> = {
    active: 'active',
    expiring: 'expiring',
    expired: 'expired',
    attention: 'with links to review',
};

export const ContractExplorer: React.FC<Props> = ({
    contracts, baseContractsById, edits, rows, statusFilter, onClearStatusFilter,
    onOpenTx, onAccept, onUnlink, onAssignTo, onSaveContract,
}) => {
    // Per-contract stats drive both the list and the detail header.
    const statsByContract = React.useMemo(() => {
        const m = new Map<string, {linked: ClReviewTx[]; pending: number; spend: number}>();
        contracts.forEach(c => m.set(c.id, {linked: [], pending: 0, spend: 0}));
        rows.forEach(tx => {
            if (!tx.effectiveContractId) return;
            const s = m.get(tx.effectiveContractId);
            if (!s) return;
            s.linked.push(tx);
            s.spend += tx.amount;
            if (tx.status === 'pending') s.pending++;
        });
        return m;
    }, [rows, contracts]);

    const sortedContracts = React.useMemo(() =>
        [...contracts].sort((a, b) => {
            const sa = statsByContract.get(a.id)!, sb = statsByContract.get(b.id)!;
            return sb.pending - sa.pending || sb.spend - sa.spend;
        }), [statsByContract, contracts]);

    const [contractSearch, setContractSearch] = React.useState('');
    const [selectedId, setSelectedId] = React.useState('');
    const [tab, setTab] = React.useState<ExplorerTab>('overview');
    const [txSearch, setTxSearch] = React.useState('');
    const [dateField, setDateField] = React.useState<DateField>('date');
    const [dateFrom, setDateFrom] = React.useState('');
    const [dateTo, setDateTo] = React.useState('');
    const [selected, setSelected] = React.useState<Set<string>>(new Set());
    const [editOpen, setEditOpen] = React.useState(false);
    const [addOpen, setAddOpen] = React.useState(false);

    const filteredContracts = React.useMemo(() => {
        const q = contractSearch.trim().toLowerCase();
        return sortedContracts.filter(c => {
            if (statusFilter === 'attention') {
                if ((statsByContract.get(c.id)?.pending ?? 0) === 0) return false;
            } else if (statusFilter && c.status !== statusFilter) {
                return false;
            }
            return !q
                || c.name.toLowerCase().includes(q)
                || c.supplier.toLowerCase().includes(q)
                || c.type.toLowerCase().includes(q);
        });
    }, [sortedContracts, contractSearch, statusFilter, statsByContract]);

    React.useEffect(() => {
        if (!filteredContracts.length) return;
        if (!filteredContracts.some(c => c.id === selectedId)) {
            setSelectedId(filteredContracts[0].id);
        }
    }, [filteredContracts, selectedId]);
    const selectContract = (id: string) => {
        setSelectedId(id);
        setTab('overview');
        setSelected(new Set());
    };
    const selectTab = (t: ExplorerTab) => {
        setTab(t);
        setSelected(new Set());
    };

    const contract = contracts.find(c => c.id === selectedId) ?? null;
    const stats = contract ? statsByContract.get(contract.id)! : null;

    // Candidates: same-supplier transactions currently not linked to this contract —
    // linked elsewhere by the AI or not linked at all. The user can pull them in.
    // Spend from other suppliers (marketplaces, payment providers, employees) is
    // reachable through "Add transactions".
    const candidates = React.useMemo(() => {
        if (!contract) return [];
        return rows
            .filter(tx => tx.supplierId === contract.supplierId && tx.effectiveContractId !== contract.id)
            .sort((a, b) => b.date.localeCompare(a.date));
    }, [rows, contract]);

    const linked = React.useMemo(
        () => (stats?.linked ?? []).slice().sort((a, b) => b.date.localeCompare(a.date)),
        [stats]);

    const visible = React.useMemo(() => {
        const base = tab === 'candidates' ? candidates : linked;
        const q = txSearch.trim().toLowerCase();
        return base.filter(tx => {
            const d = tx[dateField];
            return (!q
                    || tx.description.toLowerCase().includes(q)
                    || tx.docNumber.toLowerCase().includes(q)
                    || tx.supplier.toLowerCase().includes(q)
                    || tx.entity.toLowerCase().includes(q)
                    || tx.costCenter.toLowerCase().includes(q)
                    || tx.category.toLowerCase().includes(q)
                    || (tx.effectiveContractName ?? '').toLowerCase().includes(q))
                && (!dateFrom || (!!d && d >= dateFrom))
                && (!dateTo || (!!d && d <= dateTo));
        });
    }, [tab, linked, candidates, txSearch, dateField, dateFrom, dateTo]);

    const toggle = (id: string) => setSelected(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const allVisibleSelected = visible.length > 0 && visible.every(tx => selected.has(tx.id));
    const toggleAll = () => setSelected(
        allVisibleSelected ? new Set() : new Set(visible.map(tx => tx.id)));

    const selectedTxs = visible.filter(tx => selected.has(tx.id));
    const selSpend = selectedTxs.reduce((s, t) => s + t.amount, 0);
    const selAcceptable = selectedTxs.filter(t => t.aiContractId && t.status !== 'accepted');
    const bulk = (fn: () => void) => {
        fn();
        setSelected(new Set());
    };

    const utilization = contract && contract.contractedValue > 0 && stats
        ? stats.spend / contract.contractedValue
        : null;

    const days = contract ? daysUntilExpiry(contract) : 0;
    const contractEdited = !!contract && !!edits[contract.id];

    return (
        <div className="cl-explorer">
            {/* Left: searchable contract list */}
            <div className="cl-explorer-list">
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search contracts…"
                    value={contractSearch}
                    onChange={e => setContractSearch(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start"><SearchOutlined fontSize="small"/></InputAdornment>
                            ),
                        },
                    }}
                />
                {statusFilter && (
                    <div className="cl-explorer-listfilter">
                        Showing {STATUS_FILTER_LABEL[statusFilter]} ({formatNumber(filteredContracts.length)})
                        <button type="button" className="cl-quick-link" onClick={onClearStatusFilter}>
                            Show all
                        </button>
                    </div>
                )}
                <div className="cl-explorer-items">
                    {filteredContracts.map(c => {
                        const s = statsByContract.get(c.id)!;
                        const d = daysUntilExpiry(c);
                        return (
                            <button
                                key={c.id}
                                type="button"
                                className={`cl-explorer-item${c.id === selectedId ? ' is-active' : ''}`}
                                onClick={() => selectContract(c.id)}>
                                <span className="cl-explorer-item-top">
                                    <span className="cl-explorer-item-name">{c.name}</span>
                                    <span className={`cl-contract-status is-${c.status}`}>{c.status}</span>
                                </span>
                                <span className="cl-explorer-item-meta">
                                    {c.supplier} · {formatPeriod(c)}
                                    {c.status === 'expiring' && ` · ends in ${formatNumber(d)}d`}
                                </span>
                                <span className="cl-explorer-item-stats">
                                    {formatCompactCurrency(s.spend, CL_CURRENCY)} linked
                                    · {formatNumber(s.linked.length)} tx
                                    {s.pending > 0 &&
                                        <span className="cl-pending-badge">{formatNumber(s.pending)} to review</span>}
                                    {edits[c.id] && <span className="cl-edited-badge">edited</span>}
                                </span>
                            </button>
                        );
                    })}
                    {filteredContracts.length === 0 &&
                        <div className="cl-explorer-empty">
                            No contracts match{contractSearch ? ` “${contractSearch}”` : ' the current filter'}.
                        </div>}
                </div>
            </div>

            {/* Right: contract 360 */}
            {contract && stats && (
                <div className="cl-explorer-detail">
                    <div className="cl-explorer-head">
                        <div>
                            <h3 className="cl-explorer-title">
                                {contract.name}
                                <span className={`cl-contract-status is-${contract.status}`}>{contract.status}</span>
                                {contractEdited && <span className="cl-edited-badge">edited</span>}
                            </h3>
                            <div className="cl-explorer-subtitle">
                                {contract.supplier} · {contract.type} · {formatPeriod(contract)} · {contract.paymentTerms}
                            </div>
                        </div>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditOutlined/>}
                            onClick={() => setEditOpen(true)}>
                            Edit details
                        </Button>
                    </div>

                    <div className="cl-explorer-facts">
                        <div className="cl-explorer-fact">
                            <span className="cl-explorer-fact-label">Linked spend</span>
                            <span className="cl-explorer-fact-value">{formatCompactCurrency(stats.spend, CL_CURRENCY)}</span>
                        </div>
                        <div className="cl-explorer-fact">
                            <span className="cl-explorer-fact-label">Transactions</span>
                            <span className="cl-explorer-fact-value">{formatNumber(stats.linked.length)}</span>
                        </div>
                        <div className="cl-explorer-fact">
                            <span className="cl-explorer-fact-label">To review</span>
                            <span className="cl-explorer-fact-value">{formatNumber(stats.pending)}</span>
                        </div>
                        <div className="cl-explorer-fact">
                            <span className="cl-explorer-fact-label">Contracted value</span>
                            <span className="cl-explorer-fact-value">
                                {formatCompactCurrency(contract.contractedValue, CL_CURRENCY)}
                            </span>
                        </div>
                        <div className="cl-explorer-fact">
                            <span className="cl-explorer-fact-label">Expiry</span>
                            <span className="cl-explorer-fact-value">
                                {formatDate(contract.endDate)}
                                {contract.status !== 'active' && (
                                    <span className={`cl-daysleft is-${contract.status}`}>
                                        {days < 0 ? `${formatNumber(-days)}d ago` : `in ${formatNumber(days)}d`}
                                    </span>
                                )}
                            </span>
                        </div>
                        {utilization !== null && (
                            <div className="cl-explorer-fact cl-explorer-fact-util">
                                <span className="cl-explorer-fact-label">Utilisation</span>
                                <span className="cl-explorer-fact-value">{formatPct(utilization)}</span>
                                <span className="cl-util-track">
                                    <span className="cl-util-fill" style={{width: `${Math.min(100, utilization * 100)}%`}}/>
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="cl-explorer-tabs">
                        <button type="button" className={`cl-explorer-tab${tab === 'overview' ? ' is-active' : ''}`}
                                onClick={() => selectTab('overview')}>
                            Overview
                        </button>
                        <button type="button" className={`cl-explorer-tab${tab === 'linked' ? ' is-active' : ''}`}
                                onClick={() => selectTab('linked')}>
                            Linked transactions ({formatNumber(linked.length)})
                        </button>
                        <button type="button" className={`cl-explorer-tab${tab === 'candidates' ? ' is-active' : ''}`}
                                onClick={() => selectTab('candidates')}>
                            Candidates ({formatNumber(candidates.length)})
                        </button>
                        <span className="cl-explorer-tab-hint">
                            {tab === 'overview'
                                ? 'The contract record as extracted from the document — edit it where the AI fell short.'
                                : tab === 'linked'
                                    ? 'Transactions allocated to this contract — accept or remove links.'
                                    : `Other ${contract.supplier} transactions the AI did not put on this contract — select and link them.`}
                        </span>
                    </div>

                    {tab === 'overview' && (
                        <ContractOverview contract={contract} rows={rows} patch={edits[contract.id]}/>
                    )}

                    {tab !== 'overview' && <>
                        <div className="cl-explorer-filters">
                            <TextField
                                size="small"
                                placeholder="Search supplier, entity, cost centre…"
                                value={txSearch}
                                onChange={e => setTxSearch(e.target.value)}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start"><SearchOutlined fontSize="small"/></InputAdornment>
                                        ),
                                    },
                                }}
                            />
                            <TextField select size="small" label="Date" value={dateField}
                                       onChange={e => setDateField(e.target.value as DateField)}>
                                {DATE_FIELDS.map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
                            </TextField>
                            <TextField size="small" type="date" label="From" value={dateFrom}
                                       onChange={e => setDateFrom(e.target.value)}
                                       slotProps={{inputLabel: {shrink: true}}}/>
                            <TextField size="small" type="date" label="To" value={dateTo}
                                       onChange={e => setDateTo(e.target.value)}
                                       slotProps={{inputLabel: {shrink: true}}}/>
                            {(txSearch || dateFrom || dateTo) && (
                                <button type="button" className="cl-quick-link"
                                        onClick={() => { setTxSearch(''); setDateFrom(''); setDateTo(''); }}>
                                    Clear
                                </button>
                            )}
                            <span className="cl-explorer-count">
                                {formatNumber(visible.length)} transaction{visible.length === 1 ? '' : 's'}
                            </span>
                            {tab === 'candidates' && (
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<PlaylistAddOutlined/>}
                                    onClick={() => setAddOpen(true)}>
                                    Add transactions
                                </Button>
                            )}
                        </div>

                        <div className="cl-explorer-scroll">
                            <table className="cl-txtable">
                                <thead>
                                    <tr>
                                        <th className="cl-txtable-check">
                                            <Checkbox size="small" checked={allVisibleSelected}
                                                      indeterminate={selected.size > 0 && !allVisibleSelected}
                                                      onChange={toggleAll}/>
                                        </th>
                                        <th>Transaction</th>
                                        <th>Supplier</th>
                                        <th>Entity</th>
                                        <th>Cost centre</th>
                                        <th className="cl-txtable-num">Invoice</th>
                                        <th className="cl-txtable-num">Posted</th>
                                        <th className="cl-txtable-num">Cleared</th>
                                        <th className="cl-txtable-num">Amount</th>
                                        {tab === 'linked' ? <>
                                            <th>Confidence</th>
                                            <th>Review</th>
                                        </> : <>
                                            <th>Currently</th>
                                            <th>Period fit</th>
                                        </>}
                                        <th className="cl-txtable-action"/>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visible.map(tx => (
                                        <tr key={tx.id} className={selected.has(tx.id) ? 'is-selected' : undefined}
                                            onClick={() => onOpenTx(tx)}>
                                            <td className="cl-txtable-check" onClick={e => e.stopPropagation()}>
                                                <Checkbox size="small" checked={selected.has(tx.id)} onChange={() => toggle(tx.id)}/>
                                            </td>
                                            <td>
                                                <div className="cl-tx-desc">{tx.description}</div>
                                                <div className="cl-tx-meta">{tx.docNumber} · {tx.category}</div>
                                            </td>
                                            <td>
                                                <span className="cl-supplier-cell">
                                                    {tx.supplier}
                                                    {tx.channel && <ChannelChip channel={tx.channel}/>}
                                                </span>
                                            </td>
                                            <td>{tx.entity}</td>
                                            <td className="cl-costcenter">{tx.costCenter}</td>
                                            <td className="cl-txtable-num cl-num">{formatDate(tx.date)}</td>
                                            <td className="cl-txtable-num cl-num">{formatDate(tx.postingDate)}</td>
                                            <td className="cl-txtable-num cl-num">
                                                {tx.clearingDate
                                                    ? formatDate(tx.clearingDate)
                                                    : <span className="cl-nolink">Open</span>}
                                            </td>
                                            <td className="cl-txtable-num cl-num cl-amount">
                                                {formatFullCurrency(tx.amount, CL_CURRENCY)}
                                            </td>
                                            {tab === 'linked' ? <>
                                                <td>{tx.aiContractId
                                                    ? <ConfidencePill confidence={tx.confidence}/>
                                                    : <span className="cl-nolink">manual</span>}
                                                </td>
                                                <td><StatusChip status={tx.status}/></td>
                                                <td className="cl-txtable-action" onClick={e => e.stopPropagation()}>
                                                    {tx.aiContractId && tx.status === 'pending' && (
                                                        <Tooltip title="Accept the AI link" arrow>
                                                            <button type="button" className="cl-mini-action"
                                                                    onClick={() => onAccept([tx.id])}>
                                                                <CheckOutlined fontSize="inherit"/>
                                                            </button>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title="Remove from this contract" arrow>
                                                        <button type="button" className="cl-mini-action is-danger"
                                                                onClick={() => onUnlink([tx.id])}>
                                                            <LinkOffOutlined fontSize="inherit"/>
                                                        </button>
                                                    </Tooltip>
                                                </td>
                                            </> : <>
                                                <td>
                                                    {tx.effectiveContractName
                                                        ? <span className="cl-candidate-current">{tx.effectiveContractName}</span>
                                                        : <span className="cl-nolink">No link</span>}
                                                </td>
                                                <td>
                                                    <PeriodFit inPeriod={withinContractPeriod(tx.date, contract)}/>
                                                </td>
                                                <td className="cl-txtable-action" onClick={e => e.stopPropagation()}>
                                                    <Tooltip title="Link to this contract" arrow>
                                                        <button type="button" className="cl-mini-action is-primary"
                                                                onClick={() => onAssignTo(contract.id, [tx.id])}>
                                                            <AddLinkOutlined fontSize="inherit"/>
                                                        </button>
                                                    </Tooltip>
                                                </td>
                                            </>}
                                        </tr>
                                    ))}
                                    {visible.length === 0 && (
                                        <tr>
                                            <td colSpan={12} className="cl-txtable-empty">
                                                {tab === 'linked'
                                                    ? 'No linked transactions match the current filters.'
                                                    : <>
                                                        No candidate transactions match the current filters.{' '}
                                                        <button type="button" className="cl-quick-link"
                                                                onClick={() => setAddOpen(true)}>
                                                            Search all company spend
                                                        </button>
                                                    </>}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {tab === 'candidates' && visible.length > 0 && (
                            <div className="cl-explorer-addhint">
                                Missing something? Spend can hide behind marketplaces (Amazon), payment providers (Candex)
                                or employee reimbursements.{' '}
                                <button type="button" className="cl-quick-link" onClick={() => setAddOpen(true)}>
                                    Search all company spend
                                </button>
                            </div>
                        )}

                        {selectedTxs.length > 0 && (
                            <div className="cl-selbar">
                                <span className="cl-selbar-summary">
                                    <strong>{formatNumber(selectedTxs.length)}</strong> selected
                                    <span className="cl-selbar-sep">·</span>{formatCompactCurrency(selSpend, CL_CURRENCY)}
                                </span>
                                <span className="cl-selbar-actions">
                                    <button type="button" className="cl-selbar-clear"
                                            onClick={() => setSelected(new Set())}>Clear</button>
                                    {tab === 'linked' ? <>
                                        {selAcceptable.length > 0 && (
                                            <button type="button" className="cl-selbar-accept"
                                                    onClick={() => bulk(() => onAccept(selAcceptable.map(t => t.id)))}>
                                                <CheckOutlined fontSize="small"/>
                                                Accept {formatNumber(selAcceptable.length)}
                                            </button>
                                        )}
                                        <button type="button" className="cl-selbar-unlink"
                                                onClick={() => bulk(() => onUnlink(selectedTxs.map(t => t.id)))}>
                                            <LinkOffOutlined fontSize="small"/>
                                            Unlink
                                        </button>
                                    </> : (
                                        <button type="button" className="cl-selbar-assign"
                                                onClick={() => bulk(() => onAssignTo(contract.id, selectedTxs.map(t => t.id)))}>
                                            <AddLinkOutlined fontSize="small"/>
                                            Link {formatNumber(selectedTxs.length)} to this contract
                                        </button>
                                    )}
                                </span>
                            </div>
                        )}
                    </>}

                    <ContractEditDialog
                        open={editOpen}
                        contract={contract}
                        base={baseContractsById.get(contract.id) ?? null}
                        onSave={patch => onSaveContract(contract.id, patch)}
                        onClose={() => setEditOpen(false)}
                    />

                    <AddTransactionsDialog
                        open={addOpen}
                        contract={contract}
                        rows={rows}
                        onOpenTx={onOpenTx}
                        onAdd={txIds => {
                            onAssignTo(contract.id, txIds);
                            setAddOpen(false);
                            setTab('linked');
                        }}
                        onClose={() => setAddOpen(false)}
                    />
                </div>
            )}
        </div>
    );
};
